import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { TAction } from '../../../../../shared/frontend/scripts/actions/types/action';
import { TBuildUserApplicationSummary } from '../../../shared/types/userTypes';
import { apiServiceAsync } from '../services/apiService';
import { actions, actionTypes } from './modules';
import { applicationApiKeysListSelector, applicationSelector, applicationsListSelector } from './selectors';

const {
  setLoading,
  setLoadingApiKeys,
  setLoaded,
  setFailure,
  loadApplication,
  loadApplicationsList,
  loadApplicationApiKeysList,
  setRequestPendingKey,
  setApplicationRequest
} = actions;

function* regenerateApiKeySaga(action: TAction<{ apiKeyId: number }>): any {
  try {
    yield put(
      setRequestPendingKey({
        apiKeyId: action.payload.apiKeyId,
        requestKey: 'regenerate',
        request: { isLoading: true }
      })
    );
    const apiKey = yield call(apiServiceAsync.regenerateApiKey, action.payload.apiKeyId);

    yield put(
      loadApplicationApiKeysList({
        apiKeys: [apiKey]
      })
    );

    yield put(
      setRequestPendingKey({
        apiKeyId: action.payload.apiKeyId,
        requestKey: 'regenerate',
        request: { success: true }
      })
    );
  } catch (e) {
    yield put(
      setRequestPendingKey({
        apiKeyId: action.payload.apiKeyId,
        requestKey: 'regenerate',
        request: { error: e.message }
      })
    );
  } finally {
    yield put(
      setRequestPendingKey({
        apiKeyId: action.payload.apiKeyId,
        requestKey: 'regenerate',
        request: { isLoading: false, finishedAt: new Date() }
      })
    );
  }
}

function* generateApiKeySaga(action: TAction<number>): any {
  const apiKeyList = yield select(applicationApiKeysListSelector);

  try {
    yield put(setLoadingApiKeys(true));
    const apiKey = yield call(apiServiceAsync.generateApiKey, action.payload);

    yield put(
      loadApplicationApiKeysList({
        apiKeys: [apiKey, ...apiKeyList]
      })
    );
  } catch (e) {
    yield put(setFailure({ target: 'details', error: e.message }));
  } finally {
    yield put(setLoadingApiKeys(false));
  }
}

function* toggleBanApplicationSaga(action: TAction<any>): any {
  try {
    yield put(setApplicationRequest({ requestKey: 'ban', request: { isLoading: true } }));
    const result = yield call(apiServiceAsync.toggleBanApplication, action.payload.appId);

    if (result) {
      const applicationList = yield select(applicationsListSelector);
      const currentApplication = yield select(applicationSelector);
      const updatedApplicationList = applicationList.map((app: any) =>
        app.id === action.payload.appId ? { ...app, isActive: !app.isActive } : app
      );
      const updatedCurrentApplication =
        currentApplication?.id === action.payload.appId
          ? { ...currentApplication, isActive: !currentApplication.isActive }
          : currentApplication;

      yield put(
        loadApplicationsList({
          list: updatedApplicationList
        })
      );

      yield put(
        loadApplication({
          application: {
            ...updatedCurrentApplication
          }
        })
      );
    }
    yield put(setApplicationRequest({ requestKey: 'ban', request: { success: true } }));
  } catch (e) {
    yield put(setApplicationRequest({ requestKey: 'ban', request: { error: e.message } }));
  } finally {
    yield put(
      setApplicationRequest({
        requestKey: 'ban',
        request: { isLoading: false, finishedAt: new Date() }
      })
    );
  }
}

function* changeRateLimitSaga(action: TAction<{ apiKeyId: number; perMinuteRateLimit: number }>): any {
  try {
    yield put(
      setRequestPendingKey({
        apiKeyId: action.payload.apiKeyId,
        requestKey: 'edit',
        request: { isLoading: true }
      })
    );
    yield call(apiServiceAsync.changeRateLimitApiKey, action.payload);

    const details: TBuildUserApplicationSummary = yield select(applicationSelector);

    if (details) {
      yield put(
        loadApplication({
          application: {
            ...details,
            apiKeys: details.apiKeys?.map((apiKey) =>
              apiKey.id === action.payload.apiKeyId
                ? { ...apiKey, perMinuteRateLimit: action.payload.perMinuteRateLimit }
                : apiKey
            )
          }
        })
      );
    }

    yield put(
      setRequestPendingKey({
        apiKeyId: action.payload.apiKeyId,
        requestKey: 'edit',
        request: { success: true }
      })
    );
  } catch (e) {
    yield put(
      setRequestPendingKey({
        apiKeyId: action.payload.apiKeyId,
        requestKey: 'edit',
        request: { error: e.message }
      })
    );
  } finally {
    yield put(
      setRequestPendingKey({
        apiKeyId: action.payload.apiKeyId,
        requestKey: 'edit',
        request: { isLoading: false, finishedAt: new Date() }
      })
    );
  }
}

function* fetchApplicationsListSaga(): any {
  try {
    yield put(setLoading({ target: 'applications', status: true }));
    const applicationsList = yield call(apiServiceAsync.getApplicationsList);

    yield put(
      loadApplicationsList({
        list: applicationsList.list,
        maxCountOfApps: applicationsList.maxCountOfApps
      })
    );
    yield put(setLoaded({ target: 'applications', status: true }));
  } catch (e) {
    yield put(setFailure({ target: 'applications', error: e.message }));
  } finally {
    yield put(setLoading({ target: 'applications', status: false }));
  }
}

function* fetchApplicationSaga(action: any): any {
  try {
    yield put(setLoading({ target: 'details', status: true }));
    const application = yield call(apiServiceAsync.getApplication, action.payload);

    yield put(
      loadApplication({
        application
      })
    );
    yield put(setLoaded({ target: 'details', status: true }));
  } catch (e) {
    yield put(setFailure({ target: 'details', error: e.message }));
  } finally {
    yield put(setLoading({ target: 'details', status: false }));
  }
}

function* watchRequestGenerateApiKey(): any {
  yield takeEvery(actionTypes.GENERATE_API_KEY, generateApiKeySaga);
}
function* watchRequestRegenerateApiKey(): any {
  yield takeEvery(actionTypes.REGENERATE_API_KEY, regenerateApiKeySaga);
}

function* watchFetchApplicationsList(): any {
  yield takeLatest(actionTypes.GET_APPLICATIONS_LIST, fetchApplicationsListSaga);
}

function* watchToggleBanApplication(): any {
  yield takeLatest(actionTypes.TOGGLE_BAN_APPLICATION, toggleBanApplicationSaga);
}

function* watchChangeRateLimitApiKey(): any {
  yield takeLatest(actionTypes.CHANGE_RATE_LIMIT_API_KEY, changeRateLimitSaga);
}

function* watchFetchApplication(): any {
  yield takeLatest(actionTypes.GET_APPLICATION, fetchApplicationSaga);
}

export default [
  watchRequestRegenerateApiKey(),
  watchRequestGenerateApiKey(),
  watchFetchApplication(),
  watchFetchApplicationsList(),
  watchToggleBanApplication(),
  watchChangeRateLimitApiKey()
];
